import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {ONLY_ON_PRIMARY_LOCALE} from './localization';
import {WHEN_NOT_IN_OPTIMIZE_MODE} from './optimization';

export const canReorderElements = defineAbilityConfig([
  {
    siteModes: {design: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    abilities: {canEditStaticPageContent: true},
  },
  {
    siteModes: {design: true},
    branches: {branch: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    abilities: {canEditBranchContent: true},
  },
  {
    abilities: {isInPageBuildMode: true, canEditStaticPageContent: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
  {
    branches: {branch: true},
    abilities: {isInPageBuildMode: true, canEditBranchContent: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
] as const);

export const canAddElementToCanvas = defineAbilityConfig([
  {
    siteModes: {design: true},
    workflows: {canvas: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    abilities: {
      canEditStaticPageContent: true,
      isNotEditingCodeComponent: true,
    },
  },
  {
    // readonly mode
    userTypes: {
      unauthorized: true,
    },
    siteModes: {design: true, edit: true},
    abilities: {
      canEditStaticPageContent: true,
      isNotEditingCodeComponent: true,
    },
  },
  {
    branches: {
      branch: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    abilities: {
      canEditBranchContent: true,
      isNotEditingCodeComponent: true,
    },
  },
  {
    abilities: {
      isInPageBuildMode: true,
      canEditStaticPageContent: true,
      isNotEditingCodeComponent: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
  {
    // Campaign Build Mode - allow adding elements
    abilities: {
      isInCampaignBuildMode: true,
      canEditStaticPageContent: true,
      isNotEditingCodeComponent: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
  {
    // Campaign Build Mode - branch
    branches: {branch: true},
    abilities: {
      isInCampaignBuildMode: true,
      canEditBranchContent: true,
      isNotEditingCodeComponent: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
] as const);

export const canWrapOrUnwrapElement = defineAbilityConfig([
  {
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    abilities: {canEditStaticPageContent: true},
  },
  {
    branches: {branch: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    abilities: {canEditBranchContent: true},
  },
] as const);

export const canRemoveElement = defineAbilityConfig([
  {
    siteModes: {design: true},
    workflows: {canvas: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    abilities: {canEditStaticPageContent: true},
  },
  {
    // readonly mode
    userTypes: {
      unauthorized: true,
    },
    siteModes: {design: true, edit: true},
    abilities: {canEditStaticPageContent: true},
  },
  {
    branches: {
      branch: true,
    },
    abilities: {canEditBranchContent: true},
  },
  {
    abilities: {isInPageBuildMode: true, canEditStaticPageContent: true},
    siteModes: {design: true, edit: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
] as const);

export const canSelectMultipleElements = defineAbilityConfig({
  siteModes: {design: true, edit: false},
  workflows: {
    canvas: true,
    optimization: false,
    comment: false,
    preview: false,
  },
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  featureFlags: {
    [FEATURE_FLAGS.CANVAS_MULTI_SELECT]: true,
  },
} as const);

export const canViewMiniSettingFooter = defineAbilityConfig([
  {
    siteModes: {
      design: true,
    },
    abilities: {canEditStaticPageContent: true},
  },
] as const);

/**
 * Can the user connect or disconnect properties on components
 */
export const canConnectProperties = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    abilities: {canEditStaticPageContent: true},
    isEditingCodeComponent: false,
  },
  {
    branches: {branch: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    localization: ONLY_ON_PRIMARY_LOCALE,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    abilities: {canEditBranchContent: true},
    isEditingCodeComponent: false,
  },
] as const);

/**
 * Can the user delete component properties.
 */
export const canDeleteComponentProperties = defineAbilityConfig({
  isEditingCodeComponent: false,
} as const);

export const canEditUnboundElementInComponent = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  siteModes: {edit: true},
  workflows: {
    canvas: true,
  },
  featureFlags: {
    [FEATURE_FLAGS.REALTIME]: true,
  },
  abilities: {canModifyComponentsOnCanvas: true},
} as const);

/**
 * When true, the user can only edit content fields (text, links, images)
 * and fields bound to component props. Non-content unbound fields are hidden.
 * Currently this applies in edit site mode.
 */
export const canEditContentAndBoundFields = defineAbilityConfig({
  siteModes: {edit: true},
  branchMergeLocked: false,
} as const);

export const canResizeImageElement = defineAbilityConfig([
  {abilities: {canDesign: true, canEditStaticPageContent: true}},
  {
    branches: {branch: true},
    abilities: {canDesign: true, canEditBranchContent: true},
  },
] as const);
